import { type BffFileModelListFileModel } from '../../../generated/api-client';
import { type Asset } from './AssetsList/AssetsList';

/**
 * Updates assets state with backend files data
 *
 * @param prevAssets Current assets state
 * @param files Backend files data
 * @returns Updated assets array
 */
export function mergeAssetsWithFiles(
  prevAssets: Asset[],
  files: BffFileModelListFileModel[] | undefined,
): Asset[] {
  if (!files?.length) {
    // Keep uploading or error assets if there are no files
    return prevAssets.filter(
      (asset) => asset.isUploading || asset.isError || asset.content,
    );
  }

  // Map backend files to assets
  const backendAssets = files.map(
    (file) =>
      ({
        id: file.id,
        name: file.file_name!,
        path: file.url,
        type: file.file_type,
        creationDate: file.created_time,
        testId: `policy-file-${file.file_name}`,
        isError: false,
        isHidden: false,
        isLoading: false,
        isUploading: false,
      }) satisfies Asset,
  );

  // Keep special state assets (uploading, error) that should be preserved
  const specialStateAssets = prevAssets.filter(
    (asset) =>
      (asset.isUploading || asset.isError || asset.content) &&
      !backendAssets.some((backendAsset) => backendAsset.name === asset.name),
  );

  // Create a map of backend assets
  const backendAssetMap = new Map(
    backendAssets.map((asset) => [asset.name, asset]),
  );

  // Update existing assets with backend data
  const updatedExistingAssets = prevAssets
    .filter((asset) => backendAssetMap.has(asset.name))
    .map((asset) => {
      const backendAsset = backendAssetMap.get(asset.name);
      backendAssetMap.delete(asset.name);
      return { ...asset, ...backendAsset };
    });

  // Add remaining new backend assets
  const newAssets = Array.from(backendAssetMap.values());

  // Combine everything
  return [...updatedExistingAssets, ...specialStateAssets, ...newAssets];
}
